<template>
    <div class="space-y-6">
        <header>
            <Heading
                :level="1"
                title="Unlocked Creators"
                subtitle="View all creators that have been unlocked for your organization."
            />
        </header>

        <DiscoverTable
            ref="unlockedTable"
            :paging="paging"
            :creators="unlockedResources"
            :is-loading="isLoading"
            :is-busy="isLoading"
            :columns="columns"
            :enabled-columns="enabledColumns"
            :is-selectable="false"
            :is-sortable="false"
            id-prop="id"
            superview-path="resource"
            record-name="unlocked creators"
            @enabled-column-change="onEnabledColumnChange"
            @filter-change="onFilterChange"
            @paging-change="onPagingChange"
            @export-creators="onExportCreators"
        >
            <template #header-toolbar>
                <ColumnDisplayManager
                    :available-columns="columns"
                    :groups="columnGroups"
                    :model-value="enabledColumns"
                    @update:model-value="(newEnabledColumns) => onEnabledColumnChange(newEnabledColumns)"
                />
            </template>

            <template #header-toolbar-right>
                <div class="ml-auto">
                    <button
                        class="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-1.5 text-sm font-medium hover:bg-accent"
                        @click="$refs.unlockedTable.exportCreators()"
                    >
                        <Download class="mr-2 h-4 w-4" />Export
                    </button>
                </div>
            </template>

            <template #creator="{ field }">
                <div class="relative flex w-full max-w-full items-center">
                    <router-link
                        :to="{
                            path: route.path,
                            query: {
                                ...route.query,
                                modal: 'creator',
                                platform_id: field.platform_id,
                                platform_user_id: field.id,
                            },
                        }"
                        class="flex min-w-0 max-w-full"
                    >
                        <NamePlate
                            class="my-auto"
                            :logo="field.logo"
                            :name="field.name"
                            :display-name="field.display_name"
                            :platform-id="field.platform_id"
                            :platform-user-id="field.id"
                            :is-drawer-enabled="false"
                            :is-interactive="true"
                        />
                    </router-link>
                </div>
            </template>

            <template #followers="{ field }">
                <span>{{ field ? formatWholeNumberWithUnit(field) : '-' }}</span>
            </template>

            <template #socials="{ field }">
                <div class="flex space-x-2">
                    <template v-for="platform in ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']" :key="platform">
                        <a
                            v-if="field.socials && field.socials[platform]"
                            :href="field.socials[platform]"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center"
                        >
                            <AppIcon
                                :name="platform"
                                :class="`h-4 w-4 text-${platform}`"
                            />
                        </a>
                    </template>
                </div>
            </template>

            <template #language="{ field }">
                <Tooltip v-if="field" class="inline-block">
                    <template #trigger>
                        <div
                            class="inline-block cursor-help rounded bg-slate-100 px-2 py-1 text-xs font-semibold"
                        >
                            <span class="uppercase">{{ field }}</span>
                        </div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">{{ getLanguageFromCode(field) }}</div>
                    </template>
                </Tooltip>
            </template>

            <template #country="{ field }">
                <Tooltip v-if="field" class="inline-block">
                    <template #trigger>
                        <div
                            class="inline-block cursor-help rounded bg-slate-100 px-2 py-1 text-xs font-semibold"
                        >
                            <span class="uppercase">{{ field }}</span>
                        </div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">{{ getCountryFromCode(field) }}</div>
                    </template>
                </Tooltip>
            </template>

            <template #email="{ item }">
                <EmailUnlock
                    v-if="item && item.resource"
                    :platform-id="item.resource.platform_id"
                    :platform-user-id="item.resource._id"
                />
            </template>
        </DiscoverTable>
    </div>
</template>

<script setup>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { Download } from 'lucide-vue-next'
import { axios } from '@/utils/axios'
import { formatDateAndTime, formatWholeNumberWithUnit } from '@/utils/Utils'
import { Heading } from '@/components/ui/typography'
import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import NamePlate from '@/components/plates/NamePlate.vue'
import Tooltip from '@/components/Tooltip.vue'
import EmailUnlock from '@/components/unlocks/EmailUnlock.vue'

const paging = ref({
    totalPages: 0,
    perPage: 25,
    currentPage: 1,
    sort: [],
})

const store = useStore()
const route = useRoute()
const { getLanguageFromCode, getCountryFromCode } = store.getters

const isLoading = ref(true)
const unlockedResources = ref([])
const quotaType = ref('profile-unlock')
const columns = ref([])
const enabledColumns = ref(['normalized.name', 'normalized.email', 'normalized.followers', 'metadata.socials', 'normalized.language', 'normalized.country', 'unlocked_at'])
const columnGroups = ref([
    { id: 'basic', label: 'Basic', platforms: ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
])

const getUnlockedResources = () => {
    isLoading.value = true
    const body = {
        paging : paging.value,
        filters : [
             { 'id' : 'is_primary', 'value' : true}
        ]
    }
    axios
        .post(`/quota/unlocks/${quotaType.value}/resources`, body)
        .then(({data}) => {
            unlockedResources.value = data.payload.records
            paging.value = data.payload.pager
            isLoading.value = false
        })
}

const onFilterChange = async (updatedSort) => {
    _.assign(paging.value.sort, updatedSort)
    getUnlockedResources()
}

const onPagingChange = async(updatedPaging) => {
    _.assign(paging.value, updatedPaging)
    getUnlockedResources()
}

const onEnabledColumnChange = (newEnabledColumns) => {
    enabledColumns.value = newEnabledColumns
    store.commit('preferences:set', {
        key: 'tables.organizationunlockedcreators.columns',
        value: enabledColumns.value,
    })
}

const createColumns = () => {
    columns.value = [
        {
            id: 'normalized.name',
            key: 'creator',
            header: {
                label: 'Creator',
                is_sortable: false,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                slot: 'creator',
                slotTransformer: (item) => ({
                    id: _.get(item, 'resource._id'),
                    name: _.get(item, 'resource.normalized.name'),
                    display_name: _.get(item, 'resource.normalized.display_name'),
                    platform_id: _.get(item, 'resource.platform_id'),
                    logo: `${MEDIA_CDN_BASE}/${_.get(item, 'resource.platform_id')}/avatars/small/${_.get(item, 'resource._id')}`,
                }),
            },
            is_sticky: true,
            is_exportable: true,
            exportTransformer: (item) => ({
                ID: _.get(item, 'resource._id'),
                Name: _.get(item, 'resource.normalized.name'),
                'Platform': _.get(item, 'resource.platform_id'),
            }),
            enabled: () => {
                return true
            },
            group: 'basic',
        },
        {
            id: 'normalized.display_name',
            key: 'display_name',
            header: {
                label: 'Display Name',
                is_sortable: false,
            },
            row: {
                class: '',
                formatter: ({ field }) => field || '',
            },
            is_exportable: true,

            exportTransformer: (item) => ({
                'Display Name': _.get(item, 'resource.normalized.display_name'),
            }),
            enabled: () => true,
            group: 'basic',
        },
        {
            id: 'normalized.email',
            key: 'email',
            header: {
                label: 'Email',
                is_sortable: false,
            },
            row: {
                class: '',
                slot: 'email',
                formatter: ({ field }) => field || '',
            },
            is_exportable: true,

            exportTransformer: (item) => ({
                Email: _.get(item, 'resource.normalized.email', ''),
            }),
            enabled: () => true,
            group: 'basic',
        },
        {
            id: 'normalized.followers',
            key: 'followers',
            header: {
                label: 'Followers',
                is_sortable: false,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                slot: 'followers',
                slotTransformer: (item) => _.get(item, 'resource.normalized.followers'),
                formatter: ({ field }) => {
                    return field ? formatWholeNumberWithUnit(field) : '-'
                },
            },

            is_exportable: true,
            exportTransformer: (item) => ({
                Followers: formatExportNumber(_.get(item, 'resource.normalized.followers', null), 0),
            }),
            enabled: () => true,
            group: 'basic',
        },
        {
            id: 'metadata.socials',
            key: 'socials',
            header: {
                label: 'Socials',
                is_sortable: false,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                slot: 'socials',
                slotTransformer: (item) => ({
                    socials: _.get(item, 'resource.metadata.socials', {}),
                }),
            },

            is_exportable: true,
            exportTransformer: (item) => {
                const socials = _.get(item, 'resource.metadata.socials', {})
                const result = {};
                ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram'].forEach(platform => {
                    const platformName = platform === 'twitter' ? 'X' : _.capitalize(platform)
                    result[`${platformName} Link`] = _.get(socials, platform, '')
                })
                return result
            },
            enabled: () => true,
            group: 'basic',
        },

         {
            id: 'normalized.language',
            key: 'language',
            header: {
                label: 'Language',
                is_sortable: false,
            },
            row: {
                class: '',
                slot: 'language',
                slotTransformer: (item) => _.get(item, 'resource.normalized.language'),
            },

            is_exportable: true,
            exportTransformer: (item) => ({
                Language: _.get(item, 'resource.normalized.language', '') && getLanguageFromCode(_.get(item, 'resource.normalized.language', '')) !== 'Unknown' ? getLanguageFromCode(_.get(item, 'resource.normalized.language', '')) : '',

            }),
            group : 'basic',
        },
        {
            id: 'normalized.country',
            key: 'country',
            header: {
                label: 'Country',
                is_sortable: false,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                slot: 'country',
                slotTransformer: (item) => _.get(item, 'resource.normalized.country'),
            },

            is_exportable: true,
            exportTransformer: (item) => ({
                Country: _.get(item, 'resource.normalized.country', '') ? getCountryFromCode(_.get(item, 'resource.normalized.country', '')) : '',
            }),
            group : 'basic',
        },
        {
            id: 'unlocked_at',
            key: 'unlocked_at',
            header: {
                label: 'Unlocked On',
                is_sortable: false,
                is_hideable: false,
                class: '',
            },
            row: {
                class: '',
                formatter: ({ field }) => {
                    if (field) {
                        return formatDateAndTime(field)
                    }
                    return '-'
                },
            },
            is_exportable: true,
            enabled: () => {
                return true
            },
            exportTransformer: (item) => ({
               'Unlocked On' : _.get(item, 'unlocked_at', ''),
            }),
            group: 'basic',
        },
    ]
}

const onExportCreators = () => {
    store.commit('modal:setData', {
        modal_id: 'export_table',
        data: {
            is_open: true,
            file_name: 'Unlocked Creators',
            columns: {
                all: columns.value.filter(col =>
                    col.is_exportable
                ),
                enabled: columns.value.filter(col =>
                    enabledColumns.value.includes(col.id) && col.is_exportable
                ),
            },
            rowCount: paging.value.totalRecords,

            providerFn: async () => {
                // Fetch all data fresh for export (ignore paging)
                const body = {
                    paging: {
                        ...paging.value,
                        currentPage: 1,
                        perPage: 99999,
                    },
                    filters: [{ id: 'is_primary', value: true }]
                }

                const { data } = await axios.post(
                    `/quota/unlocks/${quotaType.value}/resources`,
                    body
                )

                return data.payload.records
            },
        },
    })
}

const formatExportNumber = (value, decimals) => {
    if (!value) {
        return null
    }

    if (decimals && decimals !== 0) {
        return value.toFixed(decimals)
    }

    return Math.round(value)
}

onMounted(() => {
    createColumns()
    getUnlockedResources()
})
</script>
